import { useRef, useEffect } from "react";
import ActionItemList from "./ActionItemList";
import type {
  Project as ProjectType,
  EditingProject,
  EditingActionItem,
} from "./types";

interface ProjectProps {
  project: ProjectType;
  projectIndex: number;
  isExpanded: boolean;
  isFocused: boolean;
  onToggleExpansion: (id: number) => void;
  hideCollapseButton?: boolean;
  onEditProject: (projectIndex: number, fieldIndex: number) => void;
  onEditActionItem: (
    projectId: number,
    actionIndex: number,
    fieldIndex: number,
  ) => void;
  onAddNewActionItem: (projectId: number) => void;
  onActionItemStatusToggle?: (
    projectId: number,
    actionIndex: number,
    newStatus: "actionable" | "done" | "not actionable",
  ) => void;
  onActionItemFieldChange: (
    field: keyof EditingActionItem,
    value: string | number,
  ) => void;
  onActionItemSave: () => void;
  onActionItemCancel: () => void;
  editingProject?: EditingProject;
  editingActionItem?: EditingActionItem;
  onProjectFieldChange?: (field: keyof EditingProject, value: string) => void;
  onProjectSave?: () => void;
  onProjectCancel?: () => void;
  actionItemFocusState: {
    itemIndex: number;
    isAddingNew: boolean;
  };
}

export default function Project({
  project,
  projectIndex,
  isExpanded,
  isFocused,
  onToggleExpansion,
  onEditProject,
  onEditActionItem,
  onAddNewActionItem,
  onActionItemStatusToggle,
  onActionItemFieldChange,
  onActionItemSave,
  onActionItemCancel,
  editingProject,
  editingActionItem,
  onProjectFieldChange,
  onProjectSave,
  onProjectCancel,
  actionItemFocusState,
  hideCollapseButton = false,
}: ProjectProps) {
  const projectNameRef = useRef<HTMLInputElement>(null);
  const projectOutcomeRef = useRef<HTMLTextAreaElement>(null);

  const isEditingThisProject = editingProject?.id === project.id;

  const handleProjectKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onProjectSave?.();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onProjectCancel?.();
    }
  };

  // Auto-focus the correct field when entering project edit mode
  useEffect(() => {
    if (isEditingThisProject && editingProject) {
      if (editingProject.fieldIndex === 1) {
        setTimeout(() => projectOutcomeRef.current?.focus(), 0);
      } else {
        setTimeout(() => projectNameRef.current?.focus(), 0);
      }
    }
  }, [isEditingThisProject, editingProject]);

  return (
    <div
      data-project-index={projectIndex}
      tabIndex={0}
      className={`border rounded-lg ${
        isFocused
          ? "ring-2 ring-blue-300 bg-blue-50 shadow-lg"
          : isExpanded
            ? "border-blue-300 shadow-md"
            : "border-gray-200 hover:border-gray-300"
      }`}
    >
      {/* Project Header */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            {!hideCollapseButton && (
              <button
                onClick={() => onToggleExpansion(project.id)}
                className="mr-3 p-1 hover:bg-gray-200 rounded"
                aria-label={isExpanded ? "Collapse project" : "Expand project"}
              >
                <svg
                  className={`w-4 h-4 ${isExpanded ? "rotate-90" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            )}

            <div className="flex-1">
              {isEditingThisProject ? (
                <div className="space-y-2">
                  <input
                    ref={projectNameRef}
                    type="text"
                    value={editingProject.name}
                    onChange={(e) =>
                      onProjectFieldChange?.("name", e.target.value)
                    }
                    onKeyDown={handleProjectKeyDown}
                    className="w-full p-2 border border-blue-500 rounded font-medium text-lg"
                  />
                  <textarea
                    ref={projectOutcomeRef}
                    value={editingProject.desiredOutcome}
                    onChange={(e) =>
                      onProjectFieldChange?.("desiredOutcome", e.target.value)
                    }
                    onKeyDown={handleProjectKeyDown}
                    className="w-full p-2 border border-blue-500 rounded resize-none"
                    rows={2}
                  />
                </div>
              ) : (
                <div>
                  <h3
                    className="text-lg font-medium mb-1 cursor-pointer hover:text-blue-600 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isExpanded) {
                        onToggleExpansion(project.id);
                      } else {
                        onEditProject(projectIndex, 0);
                      }
                    }}
                  >
                    {project.name}
                  </h3>
                  {project.desiredOutcome && (
                    <p
                      className="text-gray-600 text-sm cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isExpanded) {
                          onToggleExpansion(project.id);
                        } else {
                          onEditProject(projectIndex, 1);
                        }
                      }}
                    >
                      {project.desiredOutcome}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Items */}
      {isExpanded && (
        <div className="border-t border-gray-200 bg-gray-50">
          <div className="p-4">
            <ActionItemList
              items={project.actionItems}
              projectId={project.id}
              onAddNew={onAddNewActionItem}
              onEdit={onEditActionItem}
              onStatusToggle={onActionItemStatusToggle}
              onFieldChange={onActionItemFieldChange}
              onSave={onActionItemSave}
              onCancel={onActionItemCancel}
              focusState={actionItemFocusState}
              editingItem={editingActionItem}
              title="Action Items"
            />
          </div>
        </div>
      )}
    </div>
  );
}
